import { PUBLIC_UNLEASH_PROXY_CLIENT_URL } from '$env/static/public';

import { setContext, onMount } from 'svelte';

import { ContextStateSymbol, type IConfig } from '@unleash/proxy-client-svelte';
import { UnleashClient, type IContext } from '@unleash/proxy-client-svelte';
import { get, writable } from 'svelte/store';

import { proxyConfig } from './configs';
import type { ProxyClientContext } from '../types';
export class UnleashProxyClient {
  private startClient = true;
  private client = writable<UnleashClient | undefined>(undefined);
  private flagsReady = writable(false);
  private flagsError = writable(null);

  constructor() {}

  public init(config: IConfig = proxyConfig) {
    if (!config) {
      console.warn(
        `You must provide either a config or an unleash client to the flag provider. If you are initializing the client in useEffect, you can avoid this warning by
      checking if the client exists before rendering.`,
      );
    }
    if (!this.startClient) {
      console.info(`The client is not started. It might be already started`);
    }

    if (!get(this.client) && config) {
      this.client.set(new UnleashClient({ ...config, url: `${PUBLIC_UNLEASH_PROXY_CLIENT_URL}proxy` }));
    }

    const currentClient = get(this.client);

    currentClient?.on('ready', () => {
      this.flagsReady.set(true);
    });

    currentClient?.on('error', (e: any) => {
      this.flagsError.set(e);
    });

    onMount(() => {
      const shouldStartClient = this.startClient;
      if (shouldStartClient) {
        const currentClient = get(this.client);
        currentClient?.start();
        this.startClient = false;
      }
    });

    const updateContext = async (context: IContext): Promise<void> => {
      const currentClient = get(this.client);
      await currentClient?.updateContext(context);
    };

    const isEnabled = (name: string) => {
      const currentClient = get(this.client);
      return currentClient?.isEnabled(name);
    };

    const getVariant = (name: string) => {
      const currentClient = get(this.client);
      return currentClient?.getVariant(name);
    };

    const on = (event: string, ...args: [any, any]) => {
      const currentClient = get(this.client);
      return currentClient?.on(event, ...args);
    };

    setContext<ProxyClientContext>(ContextStateSymbol, {
      on,
      updateContext,
      isEnabled,
      getVariant,
      client: this.client,
      flagsReady: this.flagsReady,
      flagsError: this.flagsError,
    });
  }
}
